// Golden guide arrow: a big bouncing 3D arrow that points at whatever the current mission needs.
// Kids can follow it without reading anything.
import * as THREE from 'three';
import { store } from '../core/store.js';

export class GuideArrow {
  constructor(world) {
    this.world = world;
    const mat = new THREE.MeshStandardMaterial({ color: 0xffc93c, emissive: 0xcc8800, emissiveIntensity: 0.7, roughness: 0.4 });
    const g = new THREE.Group();
    const cone = new THREE.Mesh(new THREE.ConeGeometry(1.1, 2.2, 12), mat);
    cone.rotation.x = Math.PI; // point down
    cone.position.y = 1.1;
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.4, 2.2, 12), mat);
    shaft.position.y = 3.3;
    g.add(cone, shaft);
    g.visible = false;
    world.renderer.scene.add(g);
    this.arrow = g;
    this.t = 0;
  }

  _cellWorld(cx, cz) {
    const t = this.world.terrain;
    const half = t.worldSize / 2;
    const wx = (cx + 0.5) * t.cellSize - half;
    const wz = (cz + 0.5) * t.cellSize - half;
    return new THREE.Vector3(wx, t.heightAtWorld(wx, wz), wz);
  }

  _targetPos() {
    const m = store.get().mission;
    if (!m || !m.id) return null;
    const w = this.world;
    const gate = w.visitors?.gateCell;
    if (!gate) return null;
    switch (m.id) {
      case 'path':
        return this._cellWorld(gate.cx, gate.cz);
      case 'fence': {
        // a nice open spot a bit north-west of the gate
        return this._cellWorld(gate.cx - 8, gate.cz - 10);
      }
      case 'animal': case 'baby': {
        const enc = w.build.enclosures.find((e) => e.cells.size >= 9);
        if (!enc) return null;
        const [cx, cz] = [...enc.cells][Math.floor(enc.cells.size / 2)].split(',').map(Number);
        return this._cellWorld(cx, cz);
      }
      case 'stand': {
        // next to the start of the path network
        const first = [...w.build.paths][0];
        if (!first) return this._cellWorld(gate.cx + 3, gate.cz - 3);
        const [cx, cz] = first.split(',').map(Number);
        return this._cellWorld(cx + 2, cz - 2);
      }
      default:
        return null; // stat-based missions: no single place to point at
    }
  }

  update(dt) {
    this.t += dt;
    const pos = this._targetPos();
    if (!pos) { this.arrow.visible = false; return; }
    const bounce = Math.abs(Math.sin(this.t * 3)) * 1.6;
    this.arrow.position.set(pos.x, pos.y + 2.5 + bounce, pos.z);
    this.arrow.rotation.y = this.t * 1.2;
    this.arrow.visible = true;
  }

  dispose() {
    this.world.renderer.scene.remove(this.arrow);
    this.arrow.traverse((o) => {
      if (!o.isMesh) return;
      o.geometry.dispose();
      o.material.dispose();
    });
  }
}
